/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { withRouter, useHistory } from "react-router-dom";
import { Button } from "@material-ui/core";
import Page from "components/common_components/Page/Page";
import useStyles from "../useStyles";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifyWarning } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import ModulesContext from "../ModulesContext";
import { Form as FormikForm, DatePickerField, AutocompleteField } from "components/common_components/Formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import Report from "./Report";
import Resizer from "react-image-file-resizer";

const reports = [
    {
        id: "client-information",
        name: "Client Information",
        requiresDates: false,
        requiresType: false,
        onlyForAdmins: false,
    },
    {
        id: "created-receipts",
        name: "Created Receipts",
        requiresDates: true,
        requiresType: false,
        onlyForAdmins: true,
    },
    {
        id: "created-receipts-annulment",
        name: "Annulment Receipts",
        requiresDates: true,
        requiresType: false,
        onlyForAdmins: true,
    },
    { id: "labels", name: "Labels", requiresDates: false, requiresType: true, onlyForAdmins: false },
];

function Reports() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const { user } = useSelector((state) => state.Login);

    const {
        Accounting,
        currentFileCase,
        isLoading,
        module,
        clientPhoto,
        Storage,
        resources: { types },
        Reports: ReportsApi,
    } = useContext(ModulesContext);

    const [downloadLinkReady, setDownloadLinkReady] = useState(false);
    const [report, setReport] = useState(undefined);
    const [reportProps, setReportProps] = useState(undefined);

    useEffect(() => {
        if (!currentFileCase) {
            dispatch(notifyError("You have to select a client file first!"));
            history.push("/immigration/list");
        }
    }, [currentFileCase]);

    const validationSchema = Yup.lazy((values) => {
        return Yup.object({
            report: Yup.string().required("Required field"),
            initialDate: Yup.date().required("Required field"),
            finalDate: Yup.date().required("Required field"),
            type: values.report.requiresType
                ? Yup.object({
                      id: Yup.number().required("Required field"),
                  }).required("Required field")
                : Yup.object({
                      id: Yup.number().nullable().notRequired(),
                  })
                      .nullable()
                      .notRequired(),
        });
    });

    function getReportOptions() {
        if (user.isAdmin) {
            return reports;
        } else {
            return reports.filter((x) => !x.onlyForAdmins);
        }
    }

    function onCancelHandler({ setSubmitting, resetForm }) {
        setReport(undefined);
        setReportProps(undefined);
        setDownloadLinkReady(false);
        setSubmitting(false);
        resetForm({ report: getReportOptions()[0], initialDate: new Date(), finalDate: new Date() });
    }

    async function onSubmitHandler(values, { setSubmitting, setErrors, resetForm }) {
        const initialDateInMillis = values.initialDate.setHours(0, 0, 0, 0);
        const finalDateInMillis = values.finalDate.setHours(23, 59, 59, 999);
        const typeId = values.type ? values.type.id : null;

        if (Number(initialDateInMillis) > Number(finalDateInMillis)) {
            setErrors({ initialDate: "You can't set the Initial Date after the Final Date." });
            return;
        }

        if (values.report.id === "client-information") {
            const initReport = (photoUrl) => {
                setReportProps({
                    personalInfo: currentFileCase,
                    currentUser: user,
                    photo: photoUrl,
                    formName: module.formName,
                });
                setReport(values.report.id);
                setDownloadLinkReady(true);
                setSubmitting(false);
            };
            if (clientPhoto) {
                const photo = await Storage.downloadAsync({
                    dirName: "management/clients/images",
                    fileName: `client_id_${currentFileCase.clientId || currentFileCase.client.id}`,
                });
                Resizer.imageFileResizer(
                    photo, //is the file of the new image that can now be uploaded...
                    500, // is the maxWidth of the  new image
                    500, // is the maxHeight of the  new image
                    "PNG", // is the compressFormat of the  new image
                    100, // is the quality of the new image
                    0, // is the degree of clockwise rotation to apply to the image.
                    initReport, // is the callBack function of the new image URI
                    "base64" // is the output type of the new image
                );
            } else {
                initReport();
            }
        }

        if (values.report.id === "created-receipts") {
            const recepits = await Accounting.getReceiptsByDateRangeAsync(initialDateInMillis, finalDateInMillis);

            if (recepits && recepits.length > 0) {
                setReportProps({
                    receiptsData: recepits,
                    currentUser: user,
                    moduleName: module.name,
                });
                setReport(values.report.id);
                setDownloadLinkReady(true);
                setSubmitting(false);
            } else {
                dispatch(notifyWarning("No receipts found!"));
                onCancelHandler({ setSubmitting, resetForm });
            }
        }

        if (values.report.id === "created-receipts-annulment") {
            const recepits = await Accounting.getReceiptsByDateRangeAnnulmentAsync(initialDateInMillis, finalDateInMillis);

            if (recepits && recepits.length > 0) {
                setReportProps({
                    receiptsData: recepits,
                    currentUser: user,
                    moduleName: module.name,
                });
                setReport(values.report.id);
                setDownloadLinkReady(true);
                setSubmitting(false);
            } else {
                dispatch(notifyWarning("No annulment receipts found!"));
                onCancelHandler({ setSubmitting, resetForm });
            }
        }

        if (values.report.id === "labels") {
            const labels = await ReportsApi.getLabelsByTypeAsync(typeId);

            if (labels && labels.length > 0) {
                setReportProps({
                    data: labels,
                });
                setReport(values.report.id);
                setDownloadLinkReady(true);
                setSubmitting(false);
            } else {
                dispatch(notifyWarning("No labels found!"));
                onCancelHandler({ setSubmitting, resetForm });
            }
        }
    }

    const viewerEnabled = Boolean(downloadLinkReady && report && reportProps);

    return (
        <Page className={classes.root}>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <Grid container direction="row" spacing={3}>
                    <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
                        <FormikForm
                            onSubmit={onSubmitHandler}
                            validateOnChange={false}
                            initialValues={{
                                report: getReportOptions()[0],
                                initialDate: new Date(),
                                finalDate: new Date(),
                                type: null,
                            }}
                            validationSchema={validationSchema}
                            showButtons={false}
                        >
                            {({ values, ...form }) => (
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <AutocompleteField
                                            disabled={viewerEnabled}
                                            fieldName="report"
                                            label="Report *"
                                            options={getReportOptions()}
                                            valueKey="id"
                                            labelKey="name"
                                            getOptionLabel={(option) => option.name}
                                        />
                                    </Grid>
                                    {values.report && values.report.requiresDates && (
                                        <>
                                            <Grid item>
                                                <DatePickerField disabled={viewerEnabled} fieldName="initialDate" label="Initial Date *" />
                                            </Grid>
                                            <Grid item>
                                                <DatePickerField disabled={viewerEnabled} fieldName="finalDate" label="Final Date *" />
                                            </Grid>
                                        </>
                                    )}
                                    {values.report && values.report.requiresType && (
                                        <Grid item xs={12} md={12}>
                                            {types && (
                                                <AutocompleteField
                                                    fieldName="type"
                                                    label="Case Type"
                                                    options={types}
                                                    valueKey="code"
                                                    labelKey="type"
                                                    getOptionLabel={({ type }) => type}
                                                />
                                            )}
                                        </Grid>
                                    )}
                                    <Grid container spacing={1} direction="row-reverse">
                                        {!viewerEnabled && (
                                            <Button variant="text" type="submit" color="primary" endIcon={<PlayArrowIcon />}>
                                                Execute
                                            </Button>
                                        )}
                                        <Button onClick={() => onCancelHandler(form)} variant="text" type="reset" color="default">
                                            Cancel
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </FormikForm>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={9} xl={9}>
                        {viewerEnabled && <Report reportProps={reportProps} reportType={report} />}
                    </Grid>
                </Grid>
            )}
        </Page>
    );
}

export default withRouter(Reports);
